<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header flex-wrap">
      <div class="form-page-header__icon">
        <i class="pi pi-user-edit"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">تعديل بيانات الطالب</h1>
        <p class="form-page-header__subtitle" v-if="student_name">{{ student_name }}</p>
      </div>
      <a v-if="user.id" href="#periodic-follow-ups" class="btn PrimaryButton ms-auto" @click.prevent="scrollToFollowUps"><i class="pi pi-list-check" aria-hidden="true"></i> المتابعة الدورية</a>
    </div>

    <div class="container-fluid">
      <div v-if="loadingSettings" class="text-center py-5">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
      </div>

      <form v-else @submit.prevent="sendForm()">

        <!-- Student info -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-id-card"></i></div>
            <span class="form-card__title">بيانات الطالب</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">اسم الطالب <span class="required">*</span></label>
                  <InputText id="studentName" v-model="student_name" placeholder="أدخل الاسم كاملاً" class="w-100" @input="v$.student_name.$touch()" />
                  <span class="field-error" v-if="v$.student_name.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.student_name.required.$invalid">برجاء ادخال اسم الطالب</template>
                    <template v-else-if="v$.student_name.arabicOnly.$invalid">يرجى إدخال اسم الطالب باللغة العربية فقط.</template>
                    <template v-else>يجب ألا يقل اسم الطالب عن 10 أحرف</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف ولي الأمر <span class="optional">اختياري</span></label>
                  <InputText
                    id="parentPhone"
                    v-model="parent_phone"
                    inputmode="tel"
                    placeholder="01XXXXXXXXX"
                    dir="ltr"
                    class="w-100"
                    @keypress="onlyNumbers"
                    @input="v$.parent_phone.$touch()"
                  />
                  <span class="field-error" v-if="v$.parent_phone.$error">
                    <i class="pi pi-exclamation-circle"></i> رقم ولي الأمر يجب أن يتكون من 7 إلى 11 رقم.
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الجنس <span class="required">*</span></label>
                  <Dropdown v-model="selected_gender" :options="genders" showClear optionLabel="name" placeholder="اختر الجنس" class="w-100" />
                  <span class="field-error" v-if="v$.selected_gender.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار الجنس
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">نظام التعليم <span class="optional">اختياري</span></label>
                  <Dropdown v-model="selected_type" :options="types" showClear optionLabel="name" placeholder="اختر نظام التعليم" class="w-100" />
                </div>
              </div>

              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">وضع الدراسة <span class="required">*</span></label>
                  <div class="type-selector">
                    <label
                      v-for="option in studyModeOptions"
                      :key="option.value"
                      :class="['type-selector__item', { active: selected_study_mode?.value === option.value }]"
                    >
                      <input type="radio" v-model="selected_study_mode" :value="option" class="d-none" />
                      <i :class="`pi ${option.icon}`"></i>
                      {{ option.label }}
                    </label>
                  </div>
                  <span class="field-error" v-if="v$.selected_study_mode.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار وضع الدراسة
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.student_type.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.student_type.label }}
                    <span class="required" v-if="formSettings.student_type.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <Dropdown v-model="selected_student_type" :options="studentTypes" showClear optionLabel="name" placeholder="اختر نوع الطالب" class="w-100" />
                  <span class="field-error" v-if="v$.selected_student_type.$error">
                    <i class="pi pi-exclamation-circle"></i> {{ formSettings.student_type.error_required }}
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.whatsapp_number.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.whatsapp_number.label }}
                    <span class="required" v-if="formSettings.whatsapp_number.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText
                    id="whatsappNumber"
                    v-model="whatsapp_number"
                    inputmode="tel"
                    :placeholder="formSettings.whatsapp_number.placeholder"
                    dir="ltr"
                    class="w-100"
                    @keypress="onlyNumbers"
                    @input="v$.whatsapp_number.$touch()"
                  />
                  <span class="field-error" v-if="v$.whatsapp_number.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.whatsapp_number.required?.$invalid">{{ formSettings.whatsapp_number.error_required }}</template>
                    <template v-else-if="v$.whatsapp_number.exact11.$invalid">{{ formSettings.whatsapp_number.error_digits }}</template>
                    <template v-else>{{ formSettings.whatsapp_number.error_prefix }}</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.address.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.address.label }}
                    <span class="required" v-if="formSettings.address.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText id="address" v-model="address" :placeholder="formSettings.address.placeholder" class="w-100" />
                  <span class="field-error" v-if="v$.address.$error">
                    <i class="pi pi-exclamation-circle"></i> {{ formSettings.address.error_required }}
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.student_actual_grade.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.student_actual_grade.label }}
                    <span class="required" v-if="formSettings.student_actual_grade.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText
                    id="studentActualGrade"
                    v-model="student_actual_grade"
                    inputmode="decimal"
                    :placeholder="formSettings.student_actual_grade.placeholder"
                    dir="ltr"
                    class="w-100"
                    @keypress="onlyDecimal"
                    @input="v$.student_actual_grade.$touch()"
                  />
                  <span class="field-error" v-if="v$.student_actual_grade.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.student_actual_grade.required?.$invalid">{{ formSettings.student_actual_grade.error_required }}</template>
                    <template v-else-if="v$.student_actual_grade.numeric.$invalid">{{ formSettings.student_actual_grade.error_invalid }}</template>
                    <template v-else-if="v$.student_actual_grade.minValue.$invalid">{{ formSettings.student_actual_grade.error_min }}</template>
                    <template v-else-if="v$.student_actual_grade.notGreaterThanMaximum.$invalid">{{ formSettings.student_actual_grade.error_range }}</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.maximum_grade.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.maximum_grade.label }}
                    <span class="required" v-if="formSettings.maximum_grade.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText
                    id="maximumGrade"
                    v-model="maximum_grade"
                    inputmode="decimal"
                    :placeholder="formSettings.maximum_grade.placeholder"
                    dir="ltr"
                    class="w-100"
                    @keypress="onlyDecimal"
                    @input="v$.maximum_grade.$touch()"
                  />
                  <span class="field-error" v-if="v$.maximum_grade.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.maximum_grade.required?.$invalid">{{ formSettings.maximum_grade.error_required }}</template>
                    <template v-else-if="v$.maximum_grade.numeric.$invalid">{{ formSettings.maximum_grade.error_invalid }}</template>
                    <template v-else-if="v$.maximum_grade.minValue.$invalid">{{ formSettings.maximum_grade.error_min }}</template>
                    <template v-else-if="v$.maximum_grade.maxValue.$invalid">{{ formSettings.maximum_grade.error_max }}</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="isThirdPrep">
                <div class="form-field">
                  <label class="field-label">درجة الرياضيات <span class="optional">اختياري</span></label>
                  <InputText
                    id="mathDegree"
                    v-model="math_degree"
                    inputmode="decimal"
                    placeholder="الدرجة في الصف الثاني الإعدادي"
                    class="w-100"
                    @keypress="onlyDecimal"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Certificate upload -->
        <div class="form-card" v-if="formSettings.certificate_image.visible">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-file-import"></i></div>
            <span class="form-card__title">{{ formSettings.certificate_image.label }}</span>
          </div>
          <div class="form-card__body">
            <label class="field-label">رفع الشهادة <span class="optional">اختياري (اترك فارغاً للاحتفاظ بالشهادة الحالية)</span></label>
            <div :class="['upload-zone', { 'upload-zone--has-file': imageEntry }]">
              <img v-if="imageEntry" :src="imageEntry" class="upload-zone__preview" alt="صورة الشهادة" />
              <template v-else>
                <div class="upload-zone__icon">
                  <i class="pi pi-cloud-upload"></i>
                </div>
                <p class="upload-zone__label">{{ formSettings.certificate_image.upload_text }}</p>
                <p class="upload-zone__hint">{{ formSettings.certificate_image.upload_hint }}</p>
              </template>
              <input
                type="file"
                id="certificateImage"
                @change="onFileChange"
                :accept="certificateAccept"
              />
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الحفظ...' : 'حفظ التعديلات' }}
          </button>
        </div>

      </form>
      <StudentFollowUps v-if="user.id" :key="user.id" :student-id="Number(user.id)" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InputText from 'primevue/inputtext';
import Swal from 'sweetalert2';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import Dropdown from 'primevue/dropdown';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import StudentFollowUps from '@/components/follow-ups/StudentFollowUps.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';
import { scrollToFirstError } from '@/composables/useScrollToFirstError';
import { CERTIFICATE_ACCEPT, certificateUploadError, revokeCertificatePreview, validateCertificateImage } from '@/services/certificateUpload';
import {
  DEFAULT_FORM_SETTINGS,
  arabicOnly,
  digitsBetween,
  exactDigits,
  isNumericOrEmpty,
  maxValueOrEmpty,
  minValueOrEmpty,
  notGreaterThan,
  startsWithZeroOne,
  toEnglishDigits,
  trimmedMinLength,
} from '@/composables/useRegistrationValidators';

const STUDY_MODE_OPTIONS = [
  { label: 'أونلاين فقط', value: 'online', icon: 'pi-wifi' },
  { label: 'حضوري فقط', value: 'offline', icon: 'pi-building' },
  { label: 'أونلاين + حضوري', value: 'hybrid', icon: 'pi-sync' },
];

export default {
  name: "user-edit",
  data() {
    return {
      v$: useVuelidate(),
      user: {},
      student_name: "",
      parent_phone: null,
      address: "",
      math_degree: null,
      whatsapp_number: null,
      student_actual_grade: null,
      maximum_grade: null,
      imageEntry: "",
      fileData: "",
      certificateAccept: CERTIFICATE_ACCEPT,
      formSending: false,
      loadingSettings: true,
      formSettings: DEFAULT_FORM_SETTINGS,
      selected_gender: null,
      selected_type: null,
      selected_student_type: null,
      selected_study_mode: null,
      studyModeOptions: STUDY_MODE_OPTIONS,
      academic_year_id: null,
      path: null,
      genders: [
        { name: 'ولد', id: 1 },
        { name: 'بنت', id: 2 },
      ],
      types: [
        { name: 'عام', id: 1 },
        { name: 'أزهر', id: 2 },
      ],
      studentTypes: [
        { name: 'مستجد', id: 1 },
        { name: 'قديم', id: 2 },
      ],
    }
  },
  computed: {
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      if (!this.academic_year_id) return null;
      return this.gradeStore.allGrades.find(g => g.id === this.academic_year_id)
          || this.gradeStore.activeGrades.find(g => g.id === this.academic_year_id)
          || null;
    },
    isThirdPrep() {
      return this.resolvedGrade?.education_level_legacy === 3;
    },
    breadcrumbs() {
      const gradeName = this.resolvedGrade?.name ?? '';
      const gradePath = this.resolvedGrade ? `/grades/${this.resolvedGrade.slug}/students` : '/';
      this.path = gradePath;
      return [
        { name: 'الرئيسية', path: '/' },
        { name: gradeName, path: gradePath },
        { name: 'تعديل بيانات الطالب', path: this.$route.fullPath }
      ];
    }
  },
  components: {
    StudentFollowUps,
    InputText,
    Dropdown,
    Breadcrumb
  },
  validations() {
    const fs = this.formSettings;
    return {
      student_name: { required, arabicOnly, minLength10: trimmedMinLength(10) },
      parent_phone: { digitsBetween: digitsBetween(7, 11) },
      selected_gender: { required },
      selected_study_mode: { required },
      address: fs.address.required ? { required } : {},
      whatsapp_number: {
        ...(fs.whatsapp_number.required ? { required } : {}),
        exact11: exactDigits(fs.whatsapp_number.validation?.length ?? 11),
        startsWith01: startsWithZeroOne,
      },
      student_actual_grade: {
        ...(fs.student_actual_grade.required ? { required } : {}),
        numeric: isNumericOrEmpty,
        minValue: minValueOrEmpty(0),
        notGreaterThanMaximum: notGreaterThan(() => this.maximum_grade),
      },
      maximum_grade: {
        ...(fs.maximum_grade.required ? { required } : {}),
        numeric: isNumericOrEmpty,
        minValue: minValueOrEmpty(1),
        maxValue: maxValueOrEmpty(120),
      },
      // certificate_image is never enforced on edit — a file input can't be pre-filled with
      // the student's existing certificate, so re-upload must always stay optional here.
      selected_student_type: fs.student_type.required ? { required } : {},
    };
  },
  methods: {
    scrollToFollowUps() {
      this.$nextTick(() => document.getElementById('periodic-follow-ups')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    },
    async onFileChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      const result = await validateCertificateImage(file);
      if (!result.valid) {
        this.fileData = '';
        event.target.value = '';
        Swal.fire({ position: 'center', icon: 'error', title: result.message });
        return;
      }
      revokeCertificatePreview(this.imageEntry);
      this.fileData = file;
      this.imageEntry = URL.createObjectURL(file);
    },
    onlyNumbers(event) {
      const char = String.fromCharCode(event.which);
      if (!/[0-9٠-٩]/.test(char)) {
        event.preventDefault();
        return;
      }
      const value = event.target.value;
      const digitsCount = (value.match(/[0-9٠-٩]/g) || []).length;
      if (digitsCount >= 11) {
        event.preventDefault();
      }
    },
    // Grade fields (student_actual_grade / maximum_grade / math_degree) accept decimals
    // like 40.5, so allow a single '.' alongside Western/Arabic-Indic digits.
    onlyDecimal(event) {
      const char = String.fromCharCode(event.which);
      if (char === '.') {
        if (event.target.value.includes('.')) event.preventDefault();
        return;
      }
      if (!/[0-9٠-٩]/.test(char)) event.preventDefault();
    },
    toEnglishDigits(str) {
      return toEnglishDigits(str);
    },
    async fetchFormSettings() {
      if (!this.academic_year_id) {
        this.formSettings = DEFAULT_FORM_SETTINGS;
        return;
      }
      try {
        const { data } = await axios.get(`/academic-years/${this.academic_year_id}/registration-settings`);
        this.formSettings = data.form_settings;
      } catch {
        this.formSettings = DEFAULT_FORM_SETTINGS;
      }
    },
    sendForm() {
      this.v$.$validate();
      if (this.v$.$error) {
        this.$nextTick(() => scrollToFirstError());
        return;
      }
      if (!this.v$.$error) {
        const formData = new FormData();
        formData.append('user_id', this.$route.params.id);
        formData.append('student_name', this.student_name);
        if (this.parent_phone) {
          formData.append('parent_phone', this.toEnglishDigits(this.parent_phone));
        }
        formData.append('gender', this.selected_gender.id);
        if (this.selected_type) {
          formData.append('education_type', this.selected_type.id);
        }
        formData.append('study_mode', this.selected_study_mode.value);
        if (this.math_degree) {
          formData.append('math_degree', this.math_degree);
        }

        if (this.formSettings.address.visible) {
          formData.append('address', this.address || '');
        }
        if (this.formSettings.whatsapp_number.visible && this.whatsapp_number) {
          formData.append('whatsapp_number', this.toEnglishDigits(this.whatsapp_number));
        }
        if (this.formSettings.student_actual_grade.visible && this.student_actual_grade) {
          formData.append('student_actual_grade', this.toEnglishDigits(String(this.student_actual_grade).replace(/,/g, '.')));
        }
        if (this.formSettings.maximum_grade.visible && this.maximum_grade) {
          formData.append('maximum_grade', this.toEnglishDigits(String(this.maximum_grade).replace(/,/g, '.')));
        }
        if (this.formSettings.student_type.visible && this.selected_student_type) {
          formData.append('student_type', this.selected_student_type.id);
        }
        if (this.formSettings.certificate_image.visible && this.fileData instanceof File) {
          formData.append("certificate_image", this.fileData);
        }

        this.formSending = true;

        axios
            .post('/update_user_dashboard', formData)
            .then(() => {
              this.formSending = false;
              Swal.fire({
                position: "center",
                icon: "success",
                title: "تم تحديث بيانات الطالب بنجاح",
              });
              this.$router.push(this.path);
            })
            .catch((error) => {
              this.formSending = false;
              Swal.fire({
                position: "center",
                icon: "error",
                title: certificateUploadError(error),
              });
            });
      }
    },
    async fetchUserData() {
      this.loadingSettings = true;
      const formData = new FormData();
      formData.append('user_id', this.$route.params.id);

      try {
        const response = await axios.post(`/get_user_dashboard`, formData);
        this.user = response.data.user;
        this.student_name = this.user.name;
        this.parent_phone = this.user.parent_phone;
        this.address = this.user.address || '';
        this.whatsapp_number = this.user.whatsapp_number || null;
        this.student_actual_grade = this.user.student_actual_grade ?? null;
        this.maximum_grade = this.user.maximum_grade ?? null;
        this.imageEntry = this.user.certificate_link || '';

        this.selected_gender = this.genders.find(gen => gen.id === this.user.gender);
        this.selected_type = this.types.find(type => type.id === this.user.education_type);
        this.selected_student_type = this.studentTypes.find(t => t.id === this.user.student_type) || null;
        // A legacy NULL study_mode won't match any option here, so nothing gets
        // pre-selected — the admin must explicitly pick one (required validation
        // blocks saving otherwise) instead of it silently defaulting to Hybrid.
        this.selected_study_mode = this.studyModeOptions.find(m => m.value === this.user.study_mode) || null;

        this.academic_year_id = this.user.academic_year_id ?? null;

        await this.fetchFormSettings();
      } catch {
        this.user = {};
      } finally {
        this.loadingSettings = false;
      }
    },
  },
  async mounted() {
    if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    await this.fetchUserData();
  },
  beforeUnmount() {
    revokeCertificatePreview(this.imageEntry);
  },
}
</script>

<style scoped>
.upload-zone__preview {
  max-height: 180px;
  max-width: 100%;
  object-fit: contain;
  border-radius: 8px;
}
</style>
